import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../store';
import { api } from '../../utils/api';

// ── Rectify (AI) (report 990a6f19) ───────────────────────────────────────────────────────────────────
// The reporter: "Allow an AI rectification tool in the email received to enable users to improve the quality of
// attachments received by clients … name this document correctly and ask the user whether or not they would like
// to store it if the AI amended version is acceptable." Opened from a JPEG, PNG or PDF attachment of an open email
// (the chip's wand, or the preview's Rectify (AI) button). The AI straightens the document — flat, all four corners
// showing, a thin white margin all round — judges it by the passports kept in Our Documents, and
// suggests its name by the naming protocol (server: services/rectifyDoc.ts). The original and the AI version side by
// side, the name in a box to edit, and "Store this version in the Drop Folder?": Accept files the flattened copy
// there; Reject, × or Esc throws it away. The email's own attachment is never changed.
// Opening #3 ("Allow screenshots in the body of the email to be rectified as well as attachments"): the same window
// also opens from the wand HtmlFrame puts on a picture in the body, and then asks the server's body-picture doors.

type Ready = Awaited<ReturnType<typeof api.rectifyAttachment>>;

/** What is straightened: one of the email's attachments, or the n-th picture its body draws from a data: URI — a
 *  screenshot pasted into the mail, which has no attachment of its own (HtmlFrame numbers them, from 0). */
export type RectifyTarget = { att: { id: string; name: string } } | { image: number };

type Doors = {
  rectify: (lang: string) => Promise<Ready>;
  store: (token: string, name: string) => Promise<{ ok: boolean; code?: string; name?: string }>;
  discard: (token: string) => Promise<any>;
};

// The server's three calls for a target: the attachment's, or the body picture's.
function doorsOf(emailId: string, target: RectifyTarget): Doors {
  if ('att' in target) {
    const attId = target.att.id;
    return {
      rectify: lang => api.rectifyAttachment(emailId, attId, lang),
      store: (token, name) => api.storeRectified(emailId, attId, token, name),
      discard: token => api.discardRectified(emailId, attId, token),
    };
  }
  const n = target.image;
  return {
    rectify: lang => api.rectifyBodyImage(emailId, n, lang),
    store: (token, name) => api.storeRectifiedBodyImage(emailId, n, token, name),
    discard: token => api.discardRectifiedBodyImage(emailId, n, token),
  };
}

/** A JPEG, PNG or PDF — by the type the email gave it or by its name, as the preview judges a file. */
export function canRectify(type: string, name: string): boolean {
  const ct = (type || '').toLowerCase().split(';')[0].trim();
  const ext = (/\.([a-z0-9]+)$/i.exec(name || '')?.[1] || '').toLowerCase();
  return ct === 'application/pdf' || /^image\/(jpe?g|png)$/.test(ct) || ['pdf', 'jpg', 'jpeg', 'png'].includes(ext);
}

// The server's codes for an everyday outcome, to the words shown.
const FAIL_KEY: Record<string, string> = {
  unsupported: 'eml_rect_unsupported', too_large: 'eml_rect_too_large', unreadable: 'eml_rect_unreadable',
  no_document: 'eml_rect_no_document', expired: 'eml_rect_expired', bad_name: 'eml_rect_bad_name',
  no_drop_folder: 'eml_rect_no_drop', store_failed: 'eml_rect_store_failed',
};
const AMBER = '#BA7517';
const RED = '#E24B4A';

export default function RectifyModal({ emailId, target, onClose }: { emailId: string; target: RectifyTarget; onClose: () => void }) {
  const { t } = useTranslation();
  const uiLang = useStore(s => s.uiLang);
  const showToast = useStore(s => s.showToast);
  // One window is for one target (its opener keys it by the target), so its calls are fixed as it opens.
  const [doors] = useState(() => doorsOf(emailId, target));
  const fileName = 'att' in target ? target.att.name : '';   // a picture in the body has none
  const [ready, setReady] = useState<Ready | null>(null);
  const [error, setError] = useState('');
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  // The token of the copy waiting on the server until it is stored or thrown away. `started` keeps React's
  // development double run from paying for a second AI call.
  const token = useRef('');
  const started = useRef(false);
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    doors.rectify(uiLang)
      .then(r => {
        if (!r.ok || !r.token) { if (alive.current) setError(t(FAIL_KEY[r.code || ''] || 'eml_rect_failed')); return; }
        // Closed while the AI was at work: nobody is left to answer, so the copy goes.
        if (!alive.current) { void doors.discard(r.token).catch(() => {}); return; }
        token.current = r.token;
        setReady(r);
        setName(r.name || '');
      })
      .catch((e: any) => {
        if (!alive.current) return;
        // A proxy's own error page (nginx's 504) is markup, not a reason — req() flags it htmlPage.
        const why = e?.htmlPage ? '' : String(e?.message || '');
        setError(t('eml_rect_failed') + (why ? ` — ${why}` : ''));
      });
  }, [doors, uiLang, t]);

  // Reject, × and Esc: the copy waiting on the server is thrown away.
  const close = () => {
    if (token.current) void doors.discard(token.current).catch(() => {});
    token.current = '';
    onClose();
  };
  const closeRef = useRef(close);
  closeRef.current = close;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closeRef.current(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const accept = () => {
    if (!ready || !ready.token || saving) return;
    setSaving(true);
    setSaveError('');
    doors.store(ready.token, name)
      .then(r => {
        if (!alive.current) return;
        if (!r.ok) { setSaveError(t(FAIL_KEY[r.code || ''] || 'eml_rect_store_failed')); return; }
        token.current = '';   // stored: nothing left to throw away
        showToast(t('eml_rect_saved', { name: r.name || name }));
        onClose();
      })
      .catch((e: any) => {
        if (!alive.current) return;
        const why = e?.htmlPage ? '' : String(e?.message || '');
        setSaveError(t('eml_rect_store_failed') + (why ? ` — ${why}` : ''));
      })
      .finally(() => { if (alive.current) setSaving(false); });
  };

  // One picture, labelled, on a gray ground so the AI version's white margin shows. The picture is held 12px in from
  // all four sides of the ground, so a tall one (a passport's open booklet) is drawn whole and never cut off at its
  // foot.
  const pane = (label: string, src: string) => (
    <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>{label}</div>
      <div style={{ flex: 1, minHeight: 0, position: 'relative', background: 'var(--c-gray-200)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
        <img src={src} alt={label} style={{ position: 'absolute', inset: 12, maxWidth: 'calc(100% - 24px)', maxHeight: 'calc(100% - 24px)', margin: 'auto', objectFit: 'contain', border: '1px solid var(--border)' }} />
      </div>
    </div>
  );
  const button = (filled: boolean): React.CSSProperties => ({
    display: 'flex', alignItems: 'center', gap: 5, padding: '7px 16px', fontSize: 12.5, fontWeight: 600, borderRadius: 'var(--radius-md)',
    border: filled ? 'none' : '1px solid var(--border)', background: filled ? 'var(--accent)' : 'transparent',
    color: filled ? '#fff' : 'var(--text-secondary)', cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.6 : 1, flexShrink: 0,
  });

  return (
    <div data-rectify style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 5100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ width: ready ? 'min(1100px, 100%)' : 'min(440px, 100%)', height: ready ? '100%' : undefined, maxHeight: '100%', display: 'flex', flexDirection: 'column', background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-xl)', boxShadow: 'var(--shadow-md)', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px 12px 18px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
          <i className="ti ti-wand" style={{ fontSize: 20, color: 'var(--accent)', flexShrink: 0 }} aria-hidden />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text-primary)' }}>{t('eml_rect_btn')}</div>
            <div title={fileName} style={{ fontSize: 11, color: 'var(--text-tertiary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{fileName}</div>
          </div>
          <button onClick={close} title={t('eml_att_close')} style={{ width: 30, height: 30, borderRadius: '50%', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <i className="ti ti-x" style={{ fontSize: 15 }} aria-hidden />
          </button>
        </div>

        {error ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14, padding: '30px 24px 28px', textAlign: 'center' }}>
            <i className="ti ti-file-alert" style={{ fontSize: 44, color: RED }} aria-hidden />
            <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text-secondary)', lineHeight: 1.5 }}>{error}</div>
            <button onClick={close} style={button(false)}>{t('eml_att_close')}</button>
          </div>
        ) : !ready ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, padding: '34px 24px 32px' }}>
            <i className="ti ti-loader-2" style={{ fontSize: 26, color: 'var(--text-tertiary)', animation: 'spin 1s linear infinite' }} aria-hidden />
            <div style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>{t('eml_rect_working')}</div>
          </div>
        ) : (
          <>
            <div style={{ flex: 1, minHeight: 0, display: 'flex', gap: 14, padding: '14px 18px' }}>
              {pane(t('eml_rect_original'), ready.original || '')}
              {pane(t('eml_rect_ai_version'), ready.image || '')}
            </div>
            <div style={{ flexShrink: 0, padding: '12px 18px 14px', borderTop: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, fontSize: 12.5, fontWeight: 600, color: ready.acceptable ? 'var(--accent)' : AMBER }}>
                <i className={`ti ${ready.acceptable ? 'ti-circle-check' : 'ti-alert-triangle'}`} style={{ fontSize: 15, flexShrink: 0 }} aria-hidden />
                <span>
                  {t(ready.acceptable ? 'eml_rect_acceptable' : 'eml_rect_not_acceptable')}
                  {!ready.acceptable && (ready.problems || []).length > 0 && <span style={{ fontWeight: 400 }}> — {(ready.problems || []).join('; ')}</span>}
                </span>
              </div>
              {(ready.pages || 1) > 1 && <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{t('eml_rect_first_page')}</div>}
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>
                <span style={{ flexShrink: 0 }}>{t('eml_rect_name')}</span>
                <input value={name} onChange={e => { setName(e.target.value); setSaveError(''); }} placeholder={t('eml_rect_name_hint')}
                  style={{ flex: 1, minWidth: 0, padding: '7px 10px', fontSize: 13, border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', background: 'var(--bg-surface)', color: 'var(--text-primary)', outline: 'none' }} />
                <span style={{ flexShrink: 0, fontWeight: 400, color: 'var(--text-tertiary)' }}>{ready.ext}</span>
              </label>
              {saveError && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: RED }}>
                  <i className="ti ti-alert-circle" style={{ fontSize: 14, flexShrink: 0 }} aria-hidden />{saveError}
                </div>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text-primary)' }}>{t('eml_rect_ask')}</div>
                  <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>{t('eml_rect_kept')}</div>
                </div>
                <button onClick={close} disabled={saving} style={button(false)}>
                  <i className="ti ti-x" style={{ fontSize: 13 }} aria-hidden />{t('eml_rect_reject')}
                </button>
                <button onClick={accept} disabled={saving} style={button(true)}>
                  <i className={`ti ${saving ? 'ti-loader-2' : 'ti-check'}`} style={{ fontSize: 13, animation: saving ? 'spin 1s linear infinite' : undefined }} aria-hidden />{t('eml_rect_accept')}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
